import { useEffect, useMemo, useState } from 'react'
import { PrimaryButton } from '../../../components/Button'
import pxToRem from '../../../helpers/pxToRem'
import { Modal, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import {
    setFromEdit,
    setOpenRegModal,
    setProfilePhotoStep,
    setStateList,
    setStep,
} from '../../../redux/userRegistrationSlice'
import { getModalGroup } from './ModalContent'
import { isObjectEmpty } from '../../../firebase/general-apis'
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner'
import { useNavigate } from 'react-router-dom'
import { DashboardTabNames } from '../../../routers/type'
import { tabNames } from '../../../routers/helpers'
import { logger } from '../../../helpers/logger'

interface IRegistrationModals {
    buttonText?: string
    withButton?: boolean
}

export const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: pxToRem(1280),
    height: '90%',
    bgcolor: 'background.paper',
    backgroundColor: 'white',
    boxShadow: '0px 4px 4px 0px #00000040',
    borderRadius: pxToRem(16),
}

const RegistrationModals = ({
    buttonText,
    withButton,
}: IRegistrationModals) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const open = useSelector((state: RootState) => state.userReg.openRegModal)
    const step = useSelector((state: RootState) => state.userReg.step)
    const fromEdit = useSelector((state: RootState) => state.userReg.fromEdit)
    const profilePhotoStep = useSelector(
        (state: RootState) => state.userReg.profilePhotoStep
    )
    const user = useSelector((state: RootState) => state.user.user)
    const reg = useSelector((state: RootState) => state.userReg.fields)
    const schoolTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateTeachingDocuments
    )
    const eccTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateECCDocuments
    )
    const supportStaffTeachingDocuments = useSelector(
        (state: RootState) => state.userReg.stateSupportStaffDocuments
    )
    const locationState = useSelector(
        (state: RootState) => state.userReg.locationState
    )
    const employmentSetting = useSelector(
        (state: RootState) => state.userReg.employmentSetting
    )

    const [loading, setLoading] = useState(true)

    const teachingDocuments = useMemo(() => {
        return {
            school: schoolTeachingDocuments
                ? schoolTeachingDocuments[`${locationState}`] || []
                : null,
            earlyChildcare: eccTeachingDocuments
                ? eccTeachingDocuments[`${locationState}`] || []
                : null,
            supportStaff: supportStaffTeachingDocuments
                ? supportStaffTeachingDocuments[`${locationState}`] || []
                : null,
        }
    }, [
        schoolTeachingDocuments,
        eccTeachingDocuments,
        supportStaffTeachingDocuments,
        locationState,
    ])

    const [child, setChild] = useState<JSX.Element>()

    const modalList = useMemo(() => {
        if (!reg || !teachingDocuments || !employmentSetting) return []
        return getModalGroup(reg, teachingDocuments, employmentSetting)
    }, [reg, teachingDocuments, employmentSetting])

    useEffect(() => {
        dispatch(setStateList(modalList.map((el) => el.state)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalList])

    useEffect(() => {
        // if(!modalList) return;
        if (step >= 0 && step < modalList.length) {
            setChild(modalList[step].modal)
        } else if (modalList.length > 0) {
            dispatch(setOpenRegModal(false))
            dispatch(setStep(0))
            navigate(
                `${tabNames.app.teacher.index}/${DashboardTabNames.REVIEW}`
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, modalList])

    // When
    useEffect(() => {
        setLoading(true)
        if (!open) {
            modalList.length > 0 && setChild(modalList[0].modal)
            dispatch(setStep(0))
            dispatch(setFromEdit(undefined))
            return
        }
        if (fromEdit !== undefined) {
            // dispatch(setStep(listOfItems.findIndex(el => el.id === fromEdit)));
            if (!locationState) {
                return
            }
            setChild(modalList.find((el) => el.id === fromEdit)?.modal)
            let index = modalList.findIndex((el) => el.id === fromEdit)
            if (index >= 0) {
                dispatch(setStep(index))
            }
        } else if (profilePhotoStep !== undefined) {
            // dispatch(setStep(listOfItems.findIndex(el => el.id === 'profilePhoto')));
            // setChild(listOfItems.find(el => el.id === 'profilePhoto')?.modal);
            let index = modalList.findIndex((el) => el.id === 'profilePhoto')
            if (index > -1) {
                dispatch(setStep(index))
            }
            dispatch(setProfilePhotoStep(undefined))
        } else if (
            reg.accountState?.firstTime === undefined ||
            reg.accountState?.firstTime === true
        ) {
            dispatch(setStep(0))
        }

        setLoading(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    logger.debug('Modal manager rendered')

    return (
        <>
            {withButton && (
                <PrimaryButton
                    loading={user === undefined || isObjectEmpty(reg)}
                    sx={{
                        width: '80%',
                        fontSize: pxToRem(16),
                        minHeight: pxToRem(50),
                    }}
                    onClick={() => {
                        navigate(
                            `${tabNames.app.teacher.index}/${DashboardTabNames.MY_ACCOUNT}`
                        )
                    }}
                >
                    {buttonText || 'Click here'}
                </PrimaryButton>
            )}
            <Modal open={open}>
                <Stack sx={{ ...modalStyle, height: '95%', width: '95%' }}>
                    {isObjectEmpty(reg) || loading ? (
                        <LoadingSpinner />
                    ) : (
                        <>{child}</>
                    )}
                </Stack>
            </Modal>
        </>
    )
}

export default RegistrationModals
